/**
 * 订阅管理页
 *
 * 仅展示品牌订阅状态（额度池剩余、试用截止等）。
 * 订单的创建、编辑、删除、确认到账统一在「订单管理」页面处理。
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  AlertColor,
  Chip,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { AdminLayout } from '@/components/AdminLayout'
import { getBrands } from '@/api/services'
import type { Brand } from '@chaoyinli/shared'
import { formatDateOnly } from '@/utils/format'

export function SubscriptionsPage() {
  // 品牌列表
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)

  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showToast = (message: string, severity: AlertColor = 'info') => {
    setToast({ open: true, message, severity })
  }

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }))
  }

  // 加载品牌列表
  const fetchBrands = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getBrands()
      setBrands(data)
    } catch (e) {
      showToast(e instanceof Error ? e.message : '加载品牌列表失败', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  // DataGrid 列定义（品牌列表视角）
  const columns: GridColDef[] = [
    { field: 'name', headerName: '品牌名称', width: 160 },
    { field: 'sub_brand', headerName: '子品牌', width: 120, valueGetter: (v) => v || '-' },
    {
      field: 'status',
      headerName: '品牌状态',
      width: 100,
      renderCell: (params) => {
        const labels: Record<string, string> = { trial: '试用中', active: '已激活', expired: '已过期' }
        const colors: Record<string, string> = { trial: '#FF9800', active: '#4CAF50', expired: '#F44336' }
        const color = colors[params.value] || '#999'
        return (
          <Chip
            label={labels[params.value] || params.value}
            size="small"
            sx={{ backgroundColor: `${color}15`, color, fontSize: '12px' }}
          />
        )
      },
    },
    {
      field: 'quota_pool',
      headerName: '额度池剩余',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as Brand
        const total = row.quota_total ?? 0
        const used = row.quota_used ?? 0
        return (
          <Typography
            variant="body2"
            sx={{
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            剩余 {Math.max(0, total - used)} 点 / 共 {total} 点
          </Typography>
        )
      },
    },
    {
      field: 'trial_end',
      headerName: '试用截止',
      width: 120,
      valueGetter: (v) => formatDateOnly(v),
    },
  ]

  return (
    <AdminLayout>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          订阅管理
        </Typography>
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 1.5 }}>
        品牌订阅状态
      </Typography>
      <Box sx={{ height: 'calc(100vh - 200px)', minHeight: 400, backgroundColor: '#fff', borderRadius: '12px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={brands}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
            }}
          />
        )}
      </Box>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  )
}

export default SubscriptionsPage
